import React, { useEffect, useState } from "react";
import { Card, List, Spin, message, Typography, Button, Table, Space } from "antd";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import API from "api/endpoints";
import axiosInstance from "api/axiosInstance";
import { DownloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

const { Title } = Typography;

const HiringDocumentsPage = () => {
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState(null);
  const [errorText, setErrorText] = useState(null);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const employeeId = localStorage.getItem("personalId");

        if (!employeeId) {
          message.error("Employee ID not found in local storage");
          return;
        }

        const response = await axiosInstance.get(API.hiringContracts.getByEmployeeId(employeeId));

        const data = response.data;
        if (data) {
          const documentList = [
            { name: "NDA File", file: data.ndafile },
            { name: "NCA File", file: data.ncafile },
            { name: "Employee Agreement", file: data.employeeAgreement },
            { name: "I-9 Form", file: data.i_nine },
            { name: "W-4 Form", file: data.w_four },
            { name: "Offer Letter", file: data.offerLetter },
            { name: "HR Policy", file: data.hrPolicy },
          ].filter((doc) => doc.file); // Only non-empty docs

          setDocuments(documentList);
        }
      } catch (error) {
        console.error("Error fetching hiring documents:", error);
        const serverDetail = error.response?.data?.details;
        setErrorText(serverDetail);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  // Handlers for View / Download
  // Base file URL (no /api at the end)
  const fileBaseUrl = process.env.REACT_APP_Base_URL;

  //  View document in new tab
  const handlePdfPreview = (fileUrl) => {
    if (!fileUrl) return message.warning("No file found");

    const cleanUrl = `${fileBaseUrl}/${fileUrl.replace(/^\/+/, "")}`;

    window.open(cleanUrl, "_blank");
  };

  //  Download document
  //  Download document
  const handleDownload = (fileUrl, fileName) => {
    if (!fileUrl) {
      message.warning("No file to download");
      return;
    }

    try {
      // 1. Ensure we have the absolute URL
      const cleanUrl = `${fileBaseUrl}/${fileUrl.replace(/^\/+/, "")}`;

      // 2. Create a hidden <a> tag
      const link = document.createElement("a");
      link.href = cleanUrl;

      // 3. Set the download attribute
      const extension = fileUrl.split(".").pop() || "pdf";
      link.setAttribute("download", `${fileName}.${extension}`);

      // 4. Set target to _blank
      // This is crucial: it prevents CORS from blocking the request
      // by treating it as a navigation rather than a data fetch.
      link.target = "_blank";
      link.rel = "noopener noreferrer";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success("Opening download link...");
    } catch (error) {
      console.error("Download failed:", error);
      message.error("Failed to download file");
    }
  };

  const documentColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      align: "left",
      onHeaderCell: () => ({
        style: { textAlign: "left", fontWeight: 600 },
      }),
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 180,
      onHeaderCell: () => ({
        style: { textAlign: "center", fontWeight: 600 },
      }),
      render: (_, record) => (
        <Button
          className="btn-primary"
          size="small"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(record.file, record.name)}
        >
          Download
        </Button>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card style={{ margin: 24 }}>
        <Title level={4}>Hiring Documents</Title>
        <p>Click the download button next to each document to save a copy.</p>
        {errorText && (
          <div
            style={{
              backgroundColor: "#fff2f0",
              border: "1px solid #ffccc7",
              padding: "10px 15px",
              borderRadius: "8px",
              color: "#ff4d4f",
              marginBottom: "20px",
            }}
          >
            <strong>Error: </strong> {errorText}
          </div>
        )}
        {loading ? (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <Spin size="large" />
          </div>
        ) : documents ? (
          <Table dataSource={documents} columns={documentColumns} rowkey="name" />
        ) : (
          <p>No HR documents available.</p>
        )}
      </Card>
    </DashboardLayout>
  );
};

export default HiringDocumentsPage;
